import { createPortal } from 'preact/compat';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { cn } from '../../lib/utils';
import { FourCorners } from '../../lib/medievalTheme';
import { getCanvasScale, onCanvasScaleChange } from '../../utils/uiScale';
import { t } from '../../localization/t';

// Design-pixel constants. Scaled by the live canvas scale at render time
// because the menu portals to document.body (outside `.app-canvas`), so
// cqw can't resolve against the canvas. Same trade-off as `sonner.jsx`.
const DEFAULT_WIDTH = 240;
const VIEWPORT_PADDING = 12;

// Panel padding (the `p-1` on the surface) and the outer radius concentric
// with it: an item sits exactly one padding-step in from the panel edge, so
// outer = inner + pad. Items previously reused the panel's own 0.5cqw, which
// made every inner corner read fatter than the gap around it.
const PANEL_PAD = '0.25cqw';
const PANEL_RADIUS = 'var(--radius-xl)';
export const CONTEXT_MENU_ITEM_RADIUS = `calc(${PANEL_RADIUS} - ${PANEL_PAD})`;

// Visual tokens shared by every context menu surface and any sibling
// flyouts that need to read as part of the same panel family. Exported
// so the in-game right-click menu's hover sub-panels can match the
// portal'd menu pixel-for-pixel.
export const CONTEXT_MENU_PANEL_STYLE = {
  background: 'rgba(12, 10, 8, 0.96)',
  border: '1px solid rgba(180, 140, 60, 0.32)',
  borderRadius: PANEL_RADIUS,
  boxShadow: '0 24px 80px rgba(0,0,0,0.55), 0 0 24px rgba(180,140,60,0.06)',
  backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',
  isolation: 'isolate',
};

function clampPosition(position, menuWidth, padding) {
  const w = typeof window === 'undefined' ? menuWidth : window.innerWidth;
  const h = typeof window === 'undefined' ? 0 : window.innerHeight;
  return {
    left: `${Math.max(padding, Math.min(position.x || padding, w - menuWidth - padding))}px`,
    top: `${Math.max(padding, Math.min(position.y || padding, Math.max(padding, h - 120)))}px`,
  };
}

// Anchor the menu at the click point and flip its origin corner so it
// always opens toward the viewport center. Used for in-canvas right-
// click menus where the click can land anywhere on the table. The
// `viewScale` divisor + CSS `zoom` pair compensates for the parent's
// CSS zoom so the menu lands at the cursor regardless of view scale.
function flipPosition(position, viewScale) {
  const scale = viewScale || 1;
  const vh = typeof window === 'undefined' ? 0 : window.innerHeight;
  const vw = typeof window === 'undefined' ? 0 : window.innerWidth;
  const flipUp = position.y > vh * 0.55;
  const flipLeft = position.x > vw * 0.7;
  return {
    left: position.x / scale,
    top: position.y / scale,
    zoom: scale,
    transform: `translate(${flipLeft ? '-100%' : '0'}, ${flipUp ? '-100%' : '0'})`,
  };
}

function ContextMenuContent({
  ariaLabel = t('Context menu'),
  children,
  className,
  onOpenChange = () => {},
  open = false,
  position = null,
  width,                       // optional fixed width (default content-driven)
  viewScale = 1,               // applied as CSS zoom when positioning='flip'
  positioning = 'clamp',       // 'clamp' (default. For stationary UI) | 'flip' (for in-canvas)
  ornaments = false,           // render FourCorners gold accents inside the panel
  zIndex = 140,
  style,
}) {
  const contentRef = useRef(null);
  // The portal escapes `.app-canvas`, so cqw can't size the menu in lockstep
  // with the rest of the UI. Track canvas scale and convert design-px to
  // real px at render time (sonner pattern).
  const [canvasScale, setCanvasScale] = useState(getCanvasScale());
  useEffect(() => onCanvasScaleChange(setCanvasScale), []);
  const scaledWidth = (width || DEFAULT_WIDTH) * canvasScale;
  const scaledPadding = VIEWPORT_PADDING * canvasScale;
  const positionStyle = useMemo(() => {
    if (!open || !position) return null;
    if (positioning === 'flip') return flipPosition(position, viewScale);
    return clampPosition(position, scaledWidth, scaledPadding);
  }, [open, position, positioning, viewScale, scaledWidth, scaledPadding]);

  useEffect(() => {
    if (!open) return undefined;

    function handleMouseDown(event) {
      if (contentRef.current?.contains(event.target)) return;
      onOpenChange(false);
    }
    function handleKeyDown(event) {
      if (event.key === 'Escape') onOpenChange(false);
    }

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onOpenChange, open]);

  if (!open || !positionStyle || typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={contentRef}
      role="menu"
      aria-label={ariaLabel}
      className={cn('vh-menu-in fixed overflow-visible p-1', className)}
      style={{
        ...positionStyle,
        ...CONTEXT_MENU_PANEL_STYLE,
        zIndex,
        ...(width ? { width: `${scaledWidth}px` } : null),
        ...style,
      }}
    >
      {ornaments ? <FourCorners /> : null}
      {children}
    </div>,
    document.body,
  );
}

function ContextMenuLabel({ className, children, ...props }) {
  return (
    <div
      className={cn('px-3 py-1.5 text-xs font-semibold truncate select-none', className)}
      style={{ color: '#e8d5a0' }}
      {...props}
    >
      {children}
    </div>
  );
}

function ContextMenuSectionHeader({ className, children, ...props }) {
  return (
    <div
      className={cn('px-3 py-1 text-[0.625cqw] font-semibold select-none', className)}
      style={{ color: 'rgba(180,140,60,0.55)', textShadow: '0 0 12px rgba(180,140,60,0.15)' }}
      {...props}
    >
      {children}
    </div>
  );
}

const REST_FG = '#A6A09B';
const HOVER_FG = '#e8d5a0';
const HOVER_BG = 'rgba(180,140,60,0.12)';
const DANGER_REST = '#c45050';
const DANGER_HOVER = '#ff8a82';

function ContextMenuItem({
  className,
  inset = false,
  onClick,
  children,
  tone = 'default',           // 'default' | 'danger'
  ...props
}) {
  const restColor = tone === 'danger' ? DANGER_REST : REST_FG;
  const hoverColor = tone === 'danger' ? DANGER_HOVER : HOVER_FG;
  return (
    <button
      type="button"
      role="menuitem"
      className={cn(
        'flex w-full items-center text-left px-3 py-1.5 text-xs font-medium cursor-pointer',
        inset && 'pl-8',
        className,
      )}
      style={{
        color: restColor,
        background: 'transparent',
        border: 'none',
        borderRadius: CONTEXT_MENU_ITEM_RADIUS,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = HOVER_BG;
        e.currentTarget.style.color = hoverColor;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
        e.currentTarget.style.color = restColor;
      }}
      onClick={(event) => onClick?.(event)}
      {...props}
    >
      {children}
    </button>
  );
}

function ContextMenuSeparator({ className, ...props }) {
  return (
    <div
      className={cn('mx-2 my-1 h-px', className)}
      style={{ background: 'linear-gradient(90deg, transparent, rgba(180,140,60,0.32), transparent)' }}
      aria-hidden="true"
      {...props}
    />
  );
}

export {
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSectionHeader,
  ContextMenuSeparator,
};
